import { Fragment, ReactElement, useCallback, useEffect, useMemo, useState } from "react";

import { Channel, useSendbirdStateContext } from "@sendbird/uikit-react";
import "@sendbird/uikit-react/dist/index.css";
import { v4 as uuidv4 } from "uuid";
import { Modal } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useParams } from "react-router";
import "./chatStyle.scss";
import { fetchAndSetUpcomingShifts, updateChannel } from "src/modules/chat";
import { logEvent } from "@src/appV2/lib/analytics";
import { HCF_USER_EVENTS } from "src/constants/analyticsEvents";
import { ChatModalProps, ChatProviderProps } from "./chat.types";
import { isClipboardGuideChannel } from "./clipboardGuideChannel";
import { useQuery } from "@tanstack/react-query";
import { ExclusionForList, fetchExclusion } from "src/api/workerReview";
import { getLocation } from "@src/appV2/redesign/utils/routes";
import { SendbirdChat, Session, SessionType } from "src/modules/interface";
import { useMediaQuery, useTheme } from "@mui/material";
import { ChatPanel as RedesignChatPanel } from "@src/appV2/redesign/Chat/ChatPanel";
import { BottomSheet } from "@src/appV2/redesign/components/BottomSheet";
import { useIsRedesignEnabled } from "@src/appV2/redesign/hooks/useIsRedesignEnabled";
import { BerlinThemeProvider } from "@src/appV2/redesign/theme/ThemeProvider";
import { CustomTypesChannelFilter, fetchChannels, getMessageInputDefinition } from "./channel";
import SendbirdProvider from "@sendbird/uikit-react/SendbirdProvider";
import { GroupChannelHandler, UnreadChannelFilter } from "@sendbird/chat/groupChannel";
import { BaseChannel } from "@sendbird/chat";
import { SendableMessageType } from "@sendbird/uikit-react/types/utils";
import { FileMessageCreateParams, UserMessageCreateParams } from "@sendbird/chat/message";
import { renderSendbirdChannelMessage } from "./ChannelMessage";
import { environmentConfig } from "@src/appV2/environment";
import { CbhFeatureFlag, useCbhFlag } from "@src/appV2/FeatureFlags";
import { type ChartingNoncomplianceState } from "@src/appV2/Charting/api/schema";
import { useGetChartingNoncompliances } from "@src/appV2/Charting/api/useGetChartingNoncompliances";
import { ChatSearchBar } from "./ChatSearchBar";
import { debounce, uniq } from "lodash";
import { ChatHeader } from "./components/ChatHeader";
import { isDefined } from "@clipboard-health/util-ts";
import { Box, CircularProgress, Stack } from "@mui/material";
import { ChatChannelTypeFilter, ChatFilterChip } from "./components/ChatChannelTypeFilter";
import { PlacementChatHeader } from "./components/PlacementChatHeader";
import { useGetPlacementsAccessLevel } from "@src/appV2/Placements/useGetPlacementsAccessLevel";
import { useWorkplaceStarredProfessionals } from "@src/appV2/Professionals/api/starredProfessionals/useWorkplaceStarredProfessionals";
import { ModalStatus, type UseModalState, useModalState } from "@src/lib/ui-react";
import { ManageCredentialsModal } from "@src/appV2/WorkerCredentials/ManageCredentialsModal";
import { CreateOrResetCredentialsModal } from "@src/appV2/WorkerCredentials/CreateOrResetCredentialsModal";
import { ChannelPreview } from "./ChannelPreview";
import { useChatWorkerQualifications } from "./useChatWorkerQualifications";
import { ChannelMessageWrapper } from "./ChannelMessageWrapper";
import { APP_V2_APP_EVENTS, logError } from "@src/appV2/lib/analytics";
import { canFetchPlacementApplications } from "@src/appV2/PlacementApplications/api/useFetchPaginatedPlacementApplications";

const appId = environmentConfig.REACT_APP_SENDBIRD_APP_ID;

const ACTIVE_CHARTING_NONCOMPLIANCE_STATES: ChartingNoncomplianceState[] = [
  "recalled",
  "review",
  "violation",
];

function useCanAccessPlacementChats(): boolean {
  const { user, admin } = useSelector<SessionType, Session>((state) => state.session);
  return admin || canFetchPlacementApplications(user?.access ?? []);
}

function getWorkerIdFromChannelUrl(channelUrl: string, currentWorkplaceId: string) {
  const [workplaceId, workerId] = channelUrl.split("_");
  if (workplaceId !== currentWorkplaceId) {
    return undefined;
  }

  return workerId;
}

export function ChatPage() {
  const {
    userId,
    user,
    admin,
    profile,
    type: userType,
  } = useSelector<SessionType, Session>((state) => state.session);

  const sendBirdState = useSendbirdStateContext();
  const sdk = sendBirdState?.stores?.sdkStore?.sdk;

  const history = useHistory();
  const { agentId, facilityId, placementId } = useParams<{
    agentId: string;
    facilityId: string;
    placementId?: string;
  }>();
  const { channels } = useSelector<SessionType, SendbirdChat>((state) => state.chat);
  const [isChannelsLoading, setIsChannelsLoading] = useState<boolean>(false);

  const [searchQuery, setSearchQuery] = useState<string | undefined>(undefined);
  const [unreadChannelsOnly, setUnreadChannelsOnly] = useState<UnreadChannelFilter>(
    UnreadChannelFilter.ALL
  );
  const { isEnabled: isPlacementsEnabled } = useGetPlacementsAccessLevel();
  const canAccessPlacementChats = useCanAccessPlacementChats();
  const [customTypesFilter, setCustomTypesFilter] = useState<CustomTypesChannelFilter>("all");

  const currentChannelUrl = facilityId
    ? [facilityId, agentId, placementId].filter(Boolean).join("_")
    : "";
  const currentChannel = useMemo(
    () => channels.find((channel) => channel.url === currentChannelUrl),
    [channels, currentChannelUrl]
  );
  const manageCredentialsModalState = useModalState();
  const createOrResetCredentialsModalState = useModalState();
  // Redirect logic to ensure admins stay in the correct context
  // When viewing as a facility: redirect if the URL facility doesn't match the current facility context
  // When viewing as an agent: redirect if the URL agent doesn't match the current agent context
  useEffect(() => {
    const isAgentUser = userType === "AGENT";

    if (isAgentUser) {
      // Redirect when viewing as an agent and navigating to a different agent's conversation
      if (isDefined(agentId) && isDefined(userId) && agentId !== userId) {
        history.replace(getLocation("facilityChat"));
      }
    } else {
      // Redirect when viewing as a facility and navigating to a different facility's conversation
      if (isDefined(facilityId) && isDefined(userId) && facilityId !== userId) {
        history.replace(getLocation("facilityChat"));
      }
    }
  }, [history, userId, facilityId, agentId, userType]);

  const { workplaceStarredProfessionalWorkerIds, isStarredProfessionalEnabled } =
    useWorkplaceStarredProfessionals({
      workplaceId: userId,
    });

  // TODO: Remove this defensive reset once the home health starred professionals gate is no longer needed
  useEffect(() => {
    if (!isStarredProfessionalEnabled && customTypesFilter === "starred") {
      setCustomTypesFilter("all");
    }
  }, [isStarredProfessionalEnabled, customTypesFilter]);

  const chatListWorkerIds = useMemo(
    () =>
      uniq(
        channels.flatMap((channel) => {
          const channelUrl = channel.url ?? channel.lastMessage?.channelUrl;
          if (!isDefined(channelUrl) || isClipboardGuideChannel(channelUrl)) {
            return [];
          }

          const workerIdForChannel = getWorkerIdFromChannelUrl(channelUrl, userId);
          return isDefined(workerIdForChannel) ? [workerIdForChannel] : [];
        })
      ),
    [channels, userId]
  );

  const { data: chatListExclusions } = useQuery(
    ["chat", "workplace-exclusions", userId, chatListWorkerIds],
    (): Promise<ExclusionForList[]> => {
      if (chatListWorkerIds.length === 0) {
        return Promise.resolve([]);
      }

      return fetchExclusion({
        facilityId: userId,
        agentIds: chatListWorkerIds,
        pageSize: chatListWorkerIds.length,
      });
    },
    {
      enabled: userType !== "AGENT" && Boolean(userId) && chatListWorkerIds.length > 0,
    }
  );

  const blockedWorkerIds = useMemo(
    () =>
      new Set(
        (chatListExclusions ?? [])
          .filter((exclusion) => !exclusion.archived)
          .map((exclusion) => exclusion.agent.userId)
      ),
    [chatListExclusions]
  );

  const dispatch = useDispatch();

  const isCurrentChannelGuide = isClipboardGuideChannel(currentChannelUrl);

  const { defaultWorkerQualification, matchingQualifications, lastWorkedShift } =
    useChatWorkerQualifications({
      workerId: isCurrentChannelGuide ? "" : agentId,
      workplaceId: isCurrentChannelGuide ? "" : facilityId,
      currentChannel,
    });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedFetchChannels = useCallback(
    debounce(async (props) => {
      try {
        setIsChannelsLoading(true);
        await fetchChannels(props);
      } finally {
        setIsChannelsLoading(false);
      }
    }, 200),
    [fetchChannels]
  );

  useEffect(() => {
    async function fetchAndDispatchChannels() {
      if (sdk && debouncedFetchChannels) {
        const effectiveFilter =
          (customTypesFilter === "all" || customTypesFilter === "starred") &&
          !canAccessPlacementChats
            ? "flex"
            : customTypesFilter;
        void debouncedFetchChannels({
          sdk,
          dispatch,
          nicknameContainsFilter: searchQuery,
          customTypesFilter: effectiveFilter,
          unreadChannelsOnly,
        });
      }
    }
    void fetchAndDispatchChannels();
  }, [
    dispatch,
    sdk,
    searchQuery,
    debouncedFetchChannels,
    customTypesFilter,
    unreadChannelsOnly,
    canAccessPlacementChats,
  ]);

  useEffect(() => {
    if (!admin && facilityId && agentId && user?._id) {
      logEvent(HCF_USER_EVENTS.CHAT_OPENED, {
        location: "chatChannel",
        workerId: agentId,
        workplaceId: facilityId,
        workplaceUserId: user._id,
      });
    }
  }, [facilityId, agentId, user?._id, admin]);

  useEffect(() => {
    if (isDefined(profile?.userId)) {
      setCustomTypesFilter("all");
      setUnreadChannelsOnly(UnreadChannelFilter.ALL);
    }
  }, [profile?.userId]);

  const { isLoading: isLoadingExclusion, data: exclusions } = useQuery(
    ["chat", "exclusion", agentId, facilityId],
    () =>
      fetchExclusion({
        facilityId,
        agentId,
      }),
    {
      enabled: !!(userId && facilityId && agentId && !isCurrentChannelGuide),
    }
  );

  const {
    data: chartingNoncompliances,
    isLoading: isLoadingChartingNoncompliances,
    isSuccess: isSuccessChartingNoncompliances,
  } = useGetChartingNoncompliances(
    {
      workplaceId: facilityId,
      workerId: agentId,
      state: ACTIVE_CHARTING_NONCOMPLIANCE_STATES,
    },
    {
      enabled: !!(facilityId && agentId && !isCurrentChannelGuide),
    }
  );

  const workerHasChartingNonComplianceAtWorkplace =
    !isLoadingChartingNoncompliances &&
    isSuccessChartingNoncompliances &&
    (chartingNoncompliances?.data.length ?? 0) > 0;

  const isBlocked = (exclusions?.length ?? 0) > 0;

  const channelClick = (newChannelUrl: string) => {
    if (isClipboardGuideChannel(newChannelUrl)) {
      return;
    }
    const [newFacilityId, newAgentId, newPlacementId] = newChannelUrl.split("_");
    history.push(
      getLocation("facilityChatWithAgent", {
        pathParams: { facilityId: newFacilityId, agentId: newAgentId, placementId: newPlacementId },
      })
    );
  };

  return (
    <Box
      className={"chat-container chat-style"}
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
      }}
    >
      <Box className={"chat " + (currentChannelUrl ? "selected-chat" : "")}>
        <Box className="channels-container" sx={{ overflowY: "scroll" }}>
          <Box sx={{ position: "sticky", top: 0, zIndex: 1, backgroundColor: "white" }}>
            <ChatSearchBar searchQuery={searchQuery ?? ""} setSearchQuery={setSearchQuery} />
            <Stack
              direction="row"
              spacing={1}
              sx={{ padding: 1 }}
              alignItems="center"
              justifyContent="space-between"
            >
              {(isPlacementsEnabled || isStarredProfessionalEnabled) && (
                <ChatChannelTypeFilter
                  customTypesFilter={customTypesFilter}
                  onFilterChange={(filterValue) => {
                    setCustomTypesFilter(filterValue);
                    history.replace(getLocation("facilityChat"));
                  }}
                  isStarredProfessionalEnabled={isStarredProfessionalEnabled}
                  isPlacementsEnabled={isPlacementsEnabled}
                  canAccessPlacementChats={canAccessPlacementChats}
                />
              )}
              <ChatFilterChip
                label="Unread"
                isSelected={unreadChannelsOnly === UnreadChannelFilter.UNREAD_MESSAGE}
                onClick={() =>
                  setUnreadChannelsOnly(
                    unreadChannelsOnly === UnreadChannelFilter.ALL
                      ? UnreadChannelFilter.UNREAD_MESSAGE
                      : UnreadChannelFilter.ALL
                  )
                }
              />
            </Stack>
          </Box>
          {isChannelsLoading ? (
            <Stack alignItems="center" height="80%" justifyContent="center">
              <CircularProgress />
            </Stack>
          ) : (
            channels.map((channel, index, array) => {
              const channelUrl = channel.url ?? channel.lastMessage?.channelUrl;
              if (!isDefined(channelUrl)) {
                logError(APP_V2_APP_EVENTS.CHANNEL_PREVIEW_MISSING_URL, {
                  error: new Error("channel.url is undefined"),
                  metadata: { channel },
                });
                return null;
              }
              const isSelected = channelUrl === currentChannelUrl;
              const isFirst = index === 0;
              const isLast = index === array.length - 1;
              const channelWorkerId = getWorkerIdFromChannelUrl(channelUrl, userId);
              return (
                <ChannelPreview
                  key={channelUrl}
                  channelUrl={channelUrl}
                  channel={channel}
                  isSelected={isSelected}
                  isFirst={isFirst}
                  isLast={isLast}
                  customTypesFilter={customTypesFilter}
                  userType={userType}
                  currentUserId={sdk.currentUser?.userId}
                  currentChannelUrl={currentChannelUrl}
                  workplaceStarredProfessionalWorkerIds={workplaceStarredProfessionalWorkerIds}
                  handleClick={channelClick}
                  isAdmin={admin}
                  isBlocked={
                    isDefined(channelWorkerId) ? blockedWorkerIds.has(channelWorkerId) : false
                  }
                />
              );
            })
          )}
        </Box>

        <Channel
          renderMessage={({ message, chainTop, chainBottom }) => (
            <ChannelMessageWrapper message={message}>
              {renderSendbirdChannelMessage({
                message,
                chainTop,
                chainBottom,
                user,
                userId,
                currentChannel,
                channelUrl: currentChannel?.url,
                manageCredentialsModalState,
              })}
            </ChannelMessageWrapper>
          )}
          onBeforeSendFileMessage={
            sdk && user
              ? (file, quoteMessage) => handleBeforeSendFileMessage(user, file, quoteMessage, admin)
              : undefined
          }
          onBeforeSendUserMessage={
            sdk && user
              ? (text, quoteMessage) => handleBeforeSendUserMessage(user, text, quoteMessage, admin)
              : undefined
          }
          renderMessageInput={getMessageInputDefinition({
            isLoadingExclusion: isLoadingExclusion || isLoadingChartingNoncompliances,
            sdk,
            actionBy: exclusions?.[0]?.actionBy,
            isAdmin: admin,
            workerHasChartingNonComplianceAtWorkplace,
          })}
          renderCustomSeparator={() => {
            // Setting custom separator as empty object as it is rendered with ChannelMessageWrapper
            return <></>;
          }}
          disableMarkAsRead={admin}
          isReactionEnabled={false}
          channelUrl={currentChannelUrl}
          renderChannelHeader={() => {
            if (isCurrentChannelGuide) {
              return <></>;
            }

            if (currentChannel?.customType === "placements") {
              return (
                <PlacementChatHeader
                  currentChannel={currentChannel}
                  channelUrl={currentChannel.url}
                  backAction={true}
                />
              );
            }

            return (
              <ChatHeader
                key={`${agentId}_${facilityId}`}
                workerId={agentId}
                workplaceId={facilityId}
                workplaceUserId={user?._id ?? ""}
                currentChannel={currentChannel}
                backAction={true}
                facilityStatus={profile.status}
                isBlocked={isBlocked}
                manageCredentialsModalState={manageCredentialsModalState}
                matchingQualifications={matchingQualifications}
                defaultWorkerQualification={defaultWorkerQualification}
                hasWorkedAtWorkplace={isDefined(lastWorkedShift)}
              />
            );
          }}
        />
        {manageCredentialsModalState.modalIsOpen && (
          <ManageCredentialsModal
            modalState={manageCredentialsModalState}
            workerName={currentChannel?.metadata?.hcpName}
            onAction={() => {
              createOrResetCredentialsModalState.openModal();
              manageCredentialsModalState.closeModal();
            }}
            workerId={agentId}
            workplaceId={facilityId}
          />
        )}
        {createOrResetCredentialsModalState.modalIsOpen && (
          <CreateOrResetCredentialsModal
            modalState={createOrResetCredentialsModalState}
            workerName={currentChannel?.metadata?.hcpName}
            onAction={async () => {
              createOrResetCredentialsModalState.closeModal();
              manageCredentialsModalState.openModal();
            }}
            workerId={agentId}
            workplaceId={facilityId}
          />
        )}
      </Box>
    </Box>
  );
}

function RedesignedChatModal(props: ChatModalProps) {
  const { channelUrl, closeModal } = props;
  const { channels } = useSelector<SessionType, SendbirdChat>((state) => state.chat);
  const dispatch = useDispatch();
  const currentChannel = channels.find((channel) => channel.url === channelUrl);
  const [workplaceId, workerId, placementId] = (channelUrl ?? "").split("_");
  const sendBirdState = useSendbirdStateContext();
  const sdk = sendBirdState?.stores?.sdkStore?.sdk;
  const canAccessPlacementChats = useCanAccessPlacementChats();

  // Populate Redux channel list so currentChannel resolves for workerName
  useEffect(() => {
    if (isDefined(sdk)) {
      void fetchChannels({
        sdk,
        dispatch,
        customTypesFilter: canAccessPlacementChats ? undefined : "flex",
      });
    }
  }, [sdk, dispatch, canAccessPlacementChats]);

  const theme = useTheme();
  const isTabletOrSmaller = useMediaQuery(theme.breakpoints.down("md"));

  if (isClipboardGuideChannel(channelUrl)) {
    return null;
  }

  const isOpen = Boolean(channelUrl);
  const workerName = currentChannel?.metadata?.hcpName ?? currentChannel?.name ?? "Professional";

  const chatPanelModalState: UseModalState = {
    modalStatus: isOpen ? ModalStatus.OPEN : ModalStatus.CLOSED,
    setModalStatus: () => {},
    modalIsClosed: !isOpen,
    modalIsOpen: isOpen,
    openModal: () => {},
    closeModal: () => closeModal(),
  };

  if (isTabletOrSmaller) {
    return (
      <BerlinThemeProvider>
        <BottomSheet modalState={chatPanelModalState}>
          <RedesignChatPanel
            modalState={chatPanelModalState}
            workplaceId={workplaceId}
            workerId={workerId}
            workerName={workerName}
            placementId={placementId}
            channelQualification={currentChannel?.metadata?.agentReq}
          />
        </BottomSheet>
      </BerlinThemeProvider>
    );
  }

  return (
    <Modal
      className="chat-style"
      closable={false}
      destroyOnClose
      footer={null}
      visible={isOpen}
      bodyStyle={{
        height: 688,
        maxHeight: "78vh",
        padding: 0,
      }}
      width="80vw"
      style={{ maxWidth: 720 }}
      onCancel={() => closeModal()}
    >
      <BerlinThemeProvider fullHeight>
        <RedesignChatPanel
          modalState={chatPanelModalState}
          workplaceId={workplaceId}
          workerId={workerId}
          workerName={workerName}
          placementId={placementId}
          channelQualification={currentChannel?.metadata?.agentReq}
        />
      </BerlinThemeProvider>
    </Modal>
  );
}

function LegacyChatModal(props: ChatModalProps) {
  const { channelUrl, closeModal } = props;
  const { channels } = useSelector<SessionType, SendbirdChat>((state) => state.chat);
  const dispatch = useDispatch();
  const { userId, user, profile, admin } = useSelector<SessionType, Session>(
    (state) => state.session
  );
  const currentChannel = channels.find((channel) => channel.url === channelUrl);
  const isClipboardGuidesChannel = isClipboardGuideChannel(channelUrl);
  const [workplaceId, workerId] = isClipboardGuidesChannel
    ? ["", ""]
    : (channelUrl ?? "").split("_");
  const sendBirdState = useSendbirdStateContext();
  const sdk = sendBirdState?.stores?.sdkStore?.sdk;
  const canAccessPlacementChats = useCanAccessPlacementChats();

  useEffect(() => {
    if (isDefined(sdk)) {
      void fetchChannels({
        sdk,
        dispatch,
        customTypesFilter: canAccessPlacementChats ? undefined : "flex",
      });
    }
  }, [sdk, dispatch, canAccessPlacementChats]);

  const { isLoading: isLoadingExclusion, data: exclusions } = useQuery(
    ["chat", "exclusion", workerId, workplaceId],
    () =>
      fetchExclusion({
        facilityId: workplaceId,
        agentId: workerId,
      }),
    {
      enabled: !!(userId && workerId && workplaceId && !isClipboardGuidesChannel),
    }
  );

  const {
    data: chartingNoncompliancesModal,
    isLoading: isLoadingChartingNoncompliancesModal,
    isSuccess: isSuccessChartingNoncompliancesModal,
  } = useGetChartingNoncompliances(
    {
      workplaceId,
      workerId,
      state: ACTIVE_CHARTING_NONCOMPLIANCE_STATES,
    },
    {
      enabled: !!(workerId && workplaceId && !isClipboardGuidesChannel),
    }
  );

  const workerHasChartingNonComplianceAtWorkplaceModal =
    !isLoadingChartingNoncompliancesModal &&
    isSuccessChartingNoncompliancesModal &&
    (chartingNoncompliancesModal?.data.length ?? 0) > 0;

  const isBlocked = (exclusions?.length ?? 0) > 0;

  const { defaultWorkerQualification, matchingQualifications, lastWorkedShift } =
    useChatWorkerQualifications({
      workerId,
      workplaceId,
      currentChannel,
    });

  const theme = useTheme();
  const isTabletOrSmaller = useMediaQuery(theme.breakpoints.down("md"));

  return (
    <Modal
      className="chat-style"
      footer={<Fragment />}
      visible={Boolean(channelUrl)}
      bodyStyle={{
        height: isTabletOrSmaller ? "100vh" : 688,
        maxHeight: isTabletOrSmaller ? "100vh" : "78vh",
        paddingTop: isTabletOrSmaller ? 0 : "50px",
        padding: isTabletOrSmaller ? 0 : undefined,
      }}
      width={isTabletOrSmaller ? "100vw" : "80vw"}
      style={
        isTabletOrSmaller
          ? {
              top: 0,
              padding: 0,
              maxWidth: "100vw",
            }
          : undefined
      }
      onCancel={() => closeModal()}
    >
      <div className="chat-container chat-style">
        <Channel
          renderMessage={({ message, chainTop, chainBottom }) =>
            renderSendbirdChannelMessage({
              message,
              chainTop,
              chainBottom,
              user,
              userId,
              currentChannel,
              channelUrl: currentChannel?.url,
            })
          }
          renderMessageInput={getMessageInputDefinition({
            isLoadingExclusion: isLoadingExclusion || isLoadingChartingNoncompliancesModal,
            sdk,
            actionBy: exclusions?.[0]?.actionBy,
            isAdmin: admin,
            workerHasChartingNonComplianceAtWorkplace:
              workerHasChartingNonComplianceAtWorkplaceModal,
          })}
          onBeforeSendFileMessage={
            sdk && user
              ? (file, quoteMessage) => handleBeforeSendFileMessage(user, file, quoteMessage, admin)
              : undefined
          }
          onBeforeSendUserMessage={
            sdk && user
              ? (text, quoteMessage) => handleBeforeSendUserMessage(user, text, quoteMessage, admin)
              : undefined
          }
          disableMarkAsRead={admin}
          isReactionEnabled={!admin}
          channelUrl={channelUrl}
          renderChannelHeader={() =>
            currentChannel?.customType === "placements" ? (
              <PlacementChatHeader
                currentChannel={currentChannel}
                channelUrl={currentChannel.url}
                backAction={false}
              />
            ) : (
              <ChatHeader
                key={`${workerId}_${workplaceId}`}
                workerId={workerId}
                workplaceId={userId}
                workplaceUserId={user?._id ?? ""}
                currentChannel={{
                  name: currentChannel?.name ?? "Channel",
                  customType: currentChannel?.customType ?? "",
                  shift: currentChannel?.shift,
                  metadata: {
                    ...currentChannel?.metadata,
                  },
                }}
                backAction={false}
                facilityStatus={profile.status}
                isBlocked={isBlocked}
                matchingQualifications={matchingQualifications}
                defaultWorkerQualification={defaultWorkerQualification}
                hasWorkedAtWorkplace={isDefined(lastWorkedShift)}
              />
            )
          }
        />
      </div>
    </Modal>
  );
}

export function ChatModal(props: ChatModalProps) {
  const isRedesignEnabled = useIsRedesignEnabled();

  if (isRedesignEnabled) {
    return <RedesignedChatModal {...props} />;
  }

  return <LegacyChatModal {...props} />;
}

export function ChatProvider(props: ChatProviderProps) {
  const { children } = props;
  const userId = useSelector<SessionType, string | undefined>((store) => store.session.userId);
  const session = useSelector<SessionType, Session>((store) => store.session);
  const sendBirdAccessToken = useSelector<SessionType, string | undefined>(
    (store) => store.session.sendBirdAccessToken
  );
  let sendbirdAppId = appId;
  // This key is used to force re-mount when a user changes the selected workplace
  const sendbirdKey = `${userId}-${sendBirdAccessToken}`;

  if (!session.profile || session.user?.email.includes("@playwright-hcf.com")) {
    sendbirdAppId = "";
  }

  return (
    <SendbirdProvider
      key={sendbirdKey}
      appId={sendbirdAppId ?? ""}
      userId={userId ?? ""}
      nickname={session.profile?.name}
      accessToken={sendBirdAccessToken}
    >
      <ChatProviderWithStore>{children}</ChatProviderWithStore>
    </SendbirdProvider>
  );
}

function ChatProviderWithStore(props: ChatProviderProps): ReactElement {
  const { children } = props;
  const dispatch = useDispatch();
  const sendBirdState = useSendbirdStateContext();
  const sdk = sendBirdState?.stores?.sdkStore?.sdk;
  const canAccessPlacementChats = useCanAccessPlacementChats();
  const defaultCustomTypesFilter: CustomTypesChannelFilter | undefined = canAccessPlacementChats
    ? undefined
    : "flex";
  const isSendbirdConcurrentConnectionOptimizationEnabled = useCbhFlag(
    CbhFeatureFlag.SENDBIRD_CONCURRENT_CONNECTION_OPTIMIZATION,
    { defaultValue: false }
  );

  useEffect(() => {
    if (!sdk?.groupChannel) {
      return;
    }

    const uuid = uuidv4();

    const channelHandler = new GroupChannelHandler({
      onChannelChanged: (channel) => {
        if (!channel.cachedMetaData || Object.keys(channel.cachedMetaData).length === 0) {
          // sendbird will auto fetch a channel when one is created, however it will not include the metadata
          // so we need to refetch the channels to get the metadata
          void fetchChannels({ sdk, dispatch, customTypesFilter: defaultCustomTypesFilter });
        } else {
          updateChannel(dispatch, channel);
        }
      },
      onMessageReceived: (_channel: BaseChannel, message) => {
        if (message.isAdminMessage()) {
          void fetchAndSetUpcomingShifts(dispatch);
        }
      },
    });

    channelHandler.onUserReceivedInvitation = (channel) => {
      updateChannel(dispatch, channel);
      void fetchAndSetUpcomingShifts(dispatch);
    };

    sdk.groupChannel.addGroupChannelHandler(uuid, channelHandler);

    return () => sdk?.groupChannel.removeGroupChannelHandler?.(uuid);
  }, [dispatch, sdk, defaultCustomTypesFilter]);

  useEffect(() => {
    // Exit early and avoid adding listeners if the feature flag is turned off
    if (!isSendbirdConcurrentConnectionOptimizationEnabled) {
      return () => null;
    }

    const handleTabChange = async () => {
      if (document.visibilityState === "visible") {
        sdk?.setForegroundState?.();
        await fetchChannels({ sdk, dispatch, customTypesFilter: defaultCustomTypesFilter });
      } else {
        sdk?.setBackgroundState?.();
      }
    };

    const handleWindowChange = async () => {
      if (!document.hasFocus() && sdk?.connectionState === "OPEN") {
        sdk?.setBackgroundState?.();
      }

      if (document.hasFocus() && sdk?.connectionState === "CLOSED") {
        sdk?.setForegroundState?.();
        await fetchChannels({ sdk, dispatch, customTypesFilter: defaultCustomTypesFilter });
      }
    };

    // This is fired when user switches tabs
    // Internally, we check if the tab is focused, then reconnect, otherwise we disconnect
    document.addEventListener("visibilitychange", handleTabChange);

    // Moving away from current window and coming back to it is tracked via blur and focus events
    // Internally, we check if the tab is focused as the window gets focus back and reconnect, otherwise we disconnect
    window.addEventListener("blur", handleWindowChange);
    window.addEventListener("focus", handleWindowChange);

    return () => {
      document.removeEventListener("visibilitychange", handleTabChange);
      window.removeEventListener("blur", handleWindowChange);
      window.removeEventListener("focus", handleWindowChange);
    };
  }, [dispatch, isSendbirdConcurrentConnectionOptimizationEnabled, sdk, defaultCustomTypesFilter]);

  return children;
}

function handleBeforeSendUserMessage(
  user: {
    access: string[];
    _id: string;
    email: string;
    name: string;
    permissions?: string[] | undefined;
  },
  text: string,
  quoteMessage?: SendableMessageType,
  isAdmin?: boolean
): UserMessageCreateParams {
  if (isAdmin) {
    throw new Error("Admin users cannot send messages in chat.");
  }

  const message = typeof text === "string" ? text.trim() : text;
  const params: UserMessageCreateParams = {
    message,
    data: JSON.stringify({
      workplaceUserId: user._id,
      workplaceUserName: user.name,
    }),
  };

  if (quoteMessage) {
    params.isReplyToChannel = true;
    params.parentMessageId = quoteMessage.messageId;
  }

  return params;
}

function handleBeforeSendFileMessage(
  user: {
    access: string[];
    _id: string;
    email: string;
    permissions?: string[] | undefined;
    name: string;
  },
  file: File,
  quoteMessage?: SendableMessageType,
  isAdmin?: boolean
): FileMessageCreateParams {
  if (isAdmin) {
    throw new Error("Admin users cannot send messages in chat.");
  }

  const params: FileMessageCreateParams = {
    file,
    data: JSON.stringify({
      workplaceUserId: user._id,
      workplaceUserName: user.name,
    }),
  };

  if (quoteMessage) {
    params.isReplyToChannel = true;
    params.parentMessageId = quoteMessage.messageId;
  }

  return params;
}
